import React, { useEffect, useState } from 'react';

/**
 * What an approver is deciding on for an expense fund approval: how the
 * budgeted total moves, and where the variance is drawn from.
 *
 * The figures are fetched on open rather than read off the row, exactly as the
 * modal this replaces does — the register carries the item, not the contingency
 * arithmetic. The same fetched totals are handed back up as the payload's
 * totalNew/totalOld, so the approver decides on the numbers that are submitted.
 */
export default function ExpenseFundBody({ row, myViews, onExtrasChange }) {
    const [data, setData] = useState({
        projectUnallocatedAmountAvailable: 0,
        contingencyAvailable: 0,
        totalOld: 0,
        totalNew: 0,
        totalNewGlobal: 0,
        expenseable: undefined,
    });

    const [failed, setFailed] = useState(false);

    const record = row?.record ?? {};

    useEffect(() => {
        if (!record.id) {
            return;
        }

        setFailed(false);

        axios
            .get(myViews.fetch_expense_fund_approval_url.replace('EXPENSE_FUND_APPROVAL_ID', record.id))
            .then(({ data: fetched }) => {
                setData(fetched);
                onExtrasChange?.({ totals: { totalNew: fetched.totalNew, totalOld: fetched.totalOld } });
            })
            .catch(() => {
                // The zeroed defaults are NOT safe to submit: the server consumes
                // totalNew/totalOld to compute the contingency draw and writes the
                // result, so posting 0/0 corrupts a department or cost centre's
                // contingency. Approve is withheld until real figures arrive.
                setFailed(true);
                onExtrasChange?.({ totalsUnavailable: true });
            });
    }, [record.id]);

    const fromProject = Number(data.projectUnallocatedAmountAvailable) || 0;
    const fromContingency = Number(data.contingencyAvailable) || 0;
    const total = (Number(data.totalNew) || 0) + (Number(data.totalNewGlobal) || 0);
    const fromCentral = total - (Number(data.totalOld) || 0) - fromProject - fromContingency;

    if (failed) {
        return (
            <p className="modal__heading tw-text-red">
                {$t('The figures for this item could not be loaded, so it cannot be approved right now. You can still reject it.')}
            </p>
        );
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            <p className="modal__heading">
                {$t('Committing this expense will change the total budgeted amount from {{from}} to {{to}}.', {
                    from: data.totalOld,
                    to: total,
                })}
            </p>

            {record.expense?.project_id !== null && fromProject > 0 && (
                <p className="modal__heading">
                    {$t('The variance of {{amount}} will be taken from the unallocated amount available on the Project the expense belongs to.', {
                        amount: fromProject.toFixed(2),
                    })}
                </p>
            )}

            {fromContingency > 0 && (
                <p className="modal__heading">
                    {$t('The variance of {{amount}} will be taken from contingency of the {{expenseable}}.', {
                        amount: fromContingency.toFixed(2),
                        expenseable: data.expenseable,
                    })}
                </p>
            )}

            <p className="modal__heading">
                {$t('The additional amount of {{amount}} will be taken from central contingency.', { amount: fromCentral.toFixed(2) })}
            </p>
        </div>
    );
}
